/**
 * Template for the system message sent to the agent after file upload.
 *
 * Edit the TEMPLATE string below to change what the agent sees.
 * Available placeholders:
 *   {{fileCount}}    — number of files uploaded
 *   {{fileList}}     — formatted file inventory (one per line)
 *   {{totalSize}}    — human-readable total size
 *
 * Each file line is formatted as:
 *   - filename (size) — description
 */

import type { UploadResult } from '../hooks/useFileUpload';

// ─── EDIT THIS TEMPLATE ──────────────────────────────────────────────
const TEMPLATE = `<system-hint>
{{fileCount}} file(s) uploaded to the workspace:
{{fileList}}
Review the uploaded files. If the user hasn't given specific instructions, briefly acknowledge what was uploaded and ask how they'd like to proceed.
</system-hint>`;
// ─── END TEMPLATE ────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatFileLine(r: UploadResult): string {
  const size = formatBytes(r.size);
  const desc = r.description || r.mime_type || 'unknown type';
  return `- ${r.filename} (${size}) — ${desc}`;
}

export function renderUploadNotification(results: UploadResult[]): string {
  const fileList = results.map(formatFileLine).join('\n');
  const totalSize = formatBytes(results.reduce((sum, r) => sum + r.size, 0));

  return TEMPLATE
    .replace('{{fileCount}}', String(results.length))
    .replace('{{fileList}}', fileList)
    .replace('{{totalSize}}', totalSize);
}
